import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Auto-opens the edit modal for a specific record when the URL contains
 * `?focus=<id>`. Used by the Diagnostics "Open ↗" deep-links so admins can
 * jump straight from an orphan report into the relevant edit form.
 *
 * @param {Array}   records  Array of records to search (e.g. invoices, leads).
 * @param {Function} onOpen  Callback invoked once with the matching record.
 *                            Pages typically wire this to their own handleEdit.
 * @param {Object}  options
 *   key          - field used to match the focus id (default: 'id').
 *                  Fallback keys are also tried automatically:
 *                  invoice_number, receipt_no, dn_number, po_number,
 *                  entry_number, request_number, request_id.
 *   clearAfter   - if true, removes ?focus from the URL after a successful
 *                  open so refreshing the page doesn't re-trigger it
 *                  (default: true).
 */
export default function useFocusRecord(records, onOpen, options = {}) {
  const { key = 'id', clearAfter = true } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const triggered = useRef(false);

  // Keep `onOpen` in a ref so we don't trigger the effect every render
  // (handleEdit is typically a new function reference on each render).
  const onOpenRef = useRef(onOpen);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);

  useEffect(() => {
    if (triggered.current) return;
    if (!records || records.length === 0) return;

    const params = new URLSearchParams(location.search);
    const focus = params.get('focus');
    if (!focus) return;

    const fallbackKeys = [
      key, 'id', 'invoice_number', 'receipt_no', 'dn_number',
      'po_number', 'entry_number', 'request_number',
    ];
    // De-dup the keys list (caller may have passed `id` as default key).
    const tried = new Set();
    let match = null;
    for (const k of fallbackKeys) {
      if (tried.has(k)) continue;
      tried.add(k);
      // String-compare so a UUID query param matches the same UUID stored as
      // a string, even if either side has whitespace or case differences in
      // future records.
      match = records.find((r) => r && r[k] != null && String(r[k]) === String(focus));
      if (match) break;
    }
    if (!match) {
      // No match: leave the ?focus param in the URL so the next data fetch
      // (which may bring the missing record) gets another shot.
      // eslint-disable-next-line no-console
      console.warn(
        `[useFocusRecord] No record matched ?focus="${focus}" across keys`,
        Array.from(tried),
        `(scanned ${records.length} records)`
      );
      return;
    }

    triggered.current = true;
    try {
      onOpenRef.current && onOpenRef.current(match);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useFocusRecord] onOpen threw:', e);
    }

    if (clearAfter) {
      params.delete('focus');
      const newSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      );
    }
  }, [records, location.search, location.pathname, key, clearAfter, navigate]);
}
